import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Trash2, Clock, CheckCircle2, XCircle, History, BarChart2, ShieldAlert, Search, Wand2, Plus } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index = weekday()

// Mirrors backend `_compute_dynamic_breaks` (shift_freeze_module.py):
// Break 1 at start+3h (Tea 15m if before 13:00, else Lunch 45m),
// then two 15m Tea Breaks each starting 2h after the previous break ends.
const addMinutesHHMM = (hhmm, minutes) => {
  const [h, m] = (hhmm || "").split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const total = ((h * 60 + m + minutes) % (24 * 60) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};
export const computeDynamicBreaks = (startTime) => {
  const [h, m] = (startTime || "").split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return [];
  const breaks = [];
  let curStart = addMinutesHHMM(startTime, 3 * 60);
  let lunchScheduled = false;
  for (let i = 0; i < 3; i++) {
    const sh = parseInt(curStart.split(":")[0], 10);
    const isLunch = !lunchScheduled && sh >= 13;
    const curEnd = addMinutesHHMM(curStart, isLunch ? 45 : 15);
    breaks.push({ start: curStart, end: curEnd, label: isLunch ? "Lunch" : "Tea Break" });
    if (isLunch) lunchScheduled = true;
    curStart = addMinutesHHMM(curEnd, 120);
  }
  return breaks;
};

const DEFAULT_SCHEDULE = {
  days_of_week: [0, 1, 2, 3, 4],
  start_time: "09:00",
  end_time: "18:00",
  break_windows: [],
  allow_voluntary_break: true,
  allow_outside_shift_access: false,
  enabled: true,
  timezone: "",
};

// IANA timezone shortcuts for the most common shifts. Free-text entry is
// allowed via the "Other (custom)" option for one-off TZs.
const COMMON_TIMEZONES = [
  { value: "", label: "Browser default (X-User-Timezone)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST, UTC+5:30)" },
  { value: "America/New_York", label: "America/New_York (ET)" },
  { value: "America/Chicago", label: "America/Chicago (CT)" },
  { value: "America/Denver", label: "America/Denver (MT)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PT)" },
  { value: "Europe/London", label: "Europe/London (UK)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AET)" },
  { value: "UTC", label: "UTC" },
];

function RoleCard({ row, onSaved }) {
  const initial = row.schedule || { ...DEFAULT_SCHEDULE };
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const initialMode = initial.break_source === "manual" ? "manual" : "auto";
  const [breakMode, setBreakMode] = useState(initialMode);
  const [breaksResetByStart, setBreaksResetByStart] = useState(false);

  const hasSchedule = !!row.schedule;
  const autoBreaks = useMemo(() => computeDynamicBreaks(draft.start_time), [draft.start_time]);
  const breaksOutOfSync = hasSchedule && breakMode === "auto"
    && JSON.stringify((initial.break_windows || []).map(({ start, end, label }) => ({ start, end, label })))
      !== JSON.stringify(autoBreaks);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial) || breakMode !== initialMode || breaksOutOfSync;

  const switchBreakMode = (m) => {
    setBreakMode(m);
    setBreaksResetByStart(false);
    if (m === "manual") {
      setDraft((d) => ({
        ...d,
        break_windows: (d.break_windows && d.break_windows.length)
          ? d.break_windows
          : computeDynamicBreaks(d.start_time),
      }));
    }
  };

  const updateBreak = (i, field, value) => {
    setBreaksResetByStart(false);
    setDraft((d) => {
      const bw = [...(d.break_windows || [])];
      bw[i] = { ...bw[i], [field]: value };
      return { ...d, break_windows: bw };
    });
  };
  const addBreak = () => setDraft((d) => ({
    ...d,
    break_windows: [...(d.break_windows || []), { start: "13:00", end: "13:15", label: "Break" }],
  }));
  const removeBreak = (i) => setDraft((d) => ({
    ...d,
    break_windows: (d.break_windows || []).filter((_, idx) => idx !== i),
  }));

  const toggleDay = (idx) => {
    setDraft((d) => {
      const days = new Set(d.days_of_week || []);
      if (days.has(idx)) days.delete(idx); else days.add(idx);
      return { ...d, days_of_week: Array.from(days).sort() };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/shift-freeze/schedules/${encodeURIComponent(row.role_name)}`, {
        ...draft,
        break_source: breakMode === "manual" ? "manual" : "dynamic",
        break_windows: breakMode === "manual" ? (draft.break_windows || []) : autoBreaks,
      });
      toast.success(`Saved schedule for ${row.role_name}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove freeze schedule for ${row.role_name}? Users with this role will no longer be locked out.`)) return;
    setRemoving(true);
    try {
      await api.delete(`/shift-freeze/schedules/${encodeURIComponent(row.role_name)}`);
      toast.success(`Removed schedule for ${row.role_name}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="border border-border rounded-sm p-4 bg-card" data-testid={`shift-sched-card-${row.role_name}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-display text-base font-semibold">{row.role_name}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
            base: {row.base_role}{!hasSchedule && " · no freeze policy yet"}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Enabled</span>
          <Switch
            data-testid={`sched-enabled-${row.role_name}`}
            checked={draft.enabled}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-3">
        {DAY_LABELS.map((d, i) => {
          const on = (draft.days_of_week || []).includes(i);
          return (
            <button
              key={d}
              onClick={() => toggleDay(i)}
              data-testid={`sched-day-${row.role_name}-${d}`}
              className={`text-[11px] py-1.5 rounded-sm border transition-colors ${
                on
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:bg-secondary"
              }`}
              type="button"
            >
              {d}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="meta-label">Start</label>
          <Input
            type="time"
            value={draft.start_time}
            onChange={(e) => {
              const v = e.target.value;
              // Changing the start time always re-triggers the auto break rule
              // (admin spec) — even in manual mode.
              setDraft((d) => ({ ...d, start_time: v, break_windows: computeDynamicBreaks(v) }));
              if (breakMode === "manual") setBreaksResetByStart(true);
            }}
            data-testid={`sched-start-${row.role_name}`}
            className="rounded-sm h-9 mt-1"
          />
        </div>
        <div>
          <label className="meta-label">End</label>
          <Input
            type="time"
            value={draft.end_time}
            onChange={(e) => setDraft((d) => ({ ...d, end_time: e.target.value }))}
            data-testid={`sched-end-${row.role_name}`}
            className="rounded-sm h-9 mt-1"
          />
        </div>
      </div>

      {/* Duration guard — prevents saving a 21h shift by typo (14:45 → 12:00
          when meant 14:45 → 00:00). Anything > 14h is flagged; > 16h is
          hard-rejected on save by the backend safety net. */}
      {(() => {
        const [sh, sm] = (draft.start_time || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
        const [eh, em] = (draft.end_time || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
        const sMin = sh * 60 + sm;
        const eMin = eh * 60 + em;
        const dur = (eMin <= sMin) ? (24 * 60 - sMin + eMin) : (eMin - sMin);
        if (dur > 14 * 60) {
          return (
            <div className="mb-3 px-3 py-2 rounded-sm text-xs bg-amber-50 border border-amber-300 text-amber-900" data-testid={`sched-duration-warn-${row.role_name}`}>
              ⚠ Shift duration is <strong>{Math.floor(dur / 60)}h {dur % 60}m</strong>. Did you mean <strong>{draft.start_time}</strong> → <strong>00:00</strong> (midnight)? Long shifts cause breaks to fire repeatedly across the whole window.
            </div>
          );
        }
        return null;
      })()}

      <div className="mb-3">
        <label className="meta-label">Timezone</label>
        <select
          value={
            COMMON_TIMEZONES.some((t) => t.value === (draft.timezone || ""))
              ? draft.timezone || ""
              : "__custom__"
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") {
              setDraft((d) => ({ ...d, timezone: d.timezone || "" }));
            } else {
              setDraft((d) => ({ ...d, timezone: v }));
            }
          }}
          data-testid={`sched-tz-${row.role_name}`}
          className="w-full rounded-sm h-9 mt-1 text-xs border border-input bg-background px-2"
        >
          {COMMON_TIMEZONES.map((t) => (
            <option key={t.value || "_blank"} value={t.value}>{t.label}</option>
          ))}
          <option value="__custom__">Other (custom IANA name)…</option>
        </select>
        {(!COMMON_TIMEZONES.some((t) => t.value === (draft.timezone || ""))) && (
          <Input
            placeholder="e.g. Pacific/Auckland"
            value={draft.timezone || ""}
            onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))}
            className="rounded-sm h-8 mt-1 text-xs"
            data-testid={`sched-tz-custom-${row.role_name}`}
          />
        )}
        <p className="text-[10px] text-muted-foreground mt-1">
          Used to interpret the shift times. Empty = uses each user&apos;s browser timezone.
          A per-user timezone set in the Users tab still overrides this.
        </p>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="meta-label">Break Windows</span>
          <div className="flex items-center gap-1" data-testid={`sched-break-mode-${row.role_name}`}>
            {["auto", "manual"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchBreakMode(m)}
                data-testid={`sched-break-mode-${m}-${row.role_name}`}
                className={`text-[10px] px-2 py-1 rounded-sm border transition-colors ${
                  breakMode === m
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:bg-secondary"
                }`}
              >
                {m === "auto" ? "Auto" : "Manual"}
              </button>
            ))}
          </div>
        </div>
        {breakMode === "auto" ? (
          <>
            <div className="space-y-1" data-testid={`sched-auto-breaks-${row.role_name}`}>
              {autoBreaks.map((b, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr] gap-1 items-center text-xs" data-testid={`sched-break-row-${row.role_name}-${i}`}>
                  <span className="font-mono px-2 py-1.5 rounded-sm bg-secondary/50 border border-border">{b.start}</span>
                  <span className="font-mono px-2 py-1.5 rounded-sm bg-secondary/50 border border-border">{b.end}</span>
                  <span className="px-2 py-1.5 rounded-sm bg-secondary/50 border border-border">{b.label}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Breaks are computed automatically from the shift start time and update live when you change it.
              Rule: Break 1 at start +3h, then +2h gaps after each break ends. The first break starting at or
              after 13:00 is the 45-min Lunch; all other breaks are 15-min Teas. Switch to Manual to edit them.
            </p>
            {breaksOutOfSync && (
              <div className="mt-1 px-2 py-1.5 rounded-sm text-[11px] bg-amber-50 border border-amber-300 text-amber-900" data-testid={`sched-breaks-outofsync-${row.role_name}`}>
                Saved breaks differ from the auto rule — saving will replace them with the breaks shown above.
              </div>
            )}
          </>
        ) : (
          <>
            <div className="space-y-1" data-testid={`sched-manual-breaks-${row.role_name}`}>
              {(draft.break_windows || []).map((b, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1 items-center text-xs" data-testid={`sched-break-row-${row.role_name}-${i}`}>
                  <Input
                    type="time"
                    value={b.start || ""}
                    onChange={(e) => updateBreak(i, "start", e.target.value)}
                    className="h-8 rounded-sm text-xs font-mono"
                    data-testid={`sched-break-start-${row.role_name}-${i}`}
                  />
                  <Input
                    type="time"
                    value={b.end || ""}
                    onChange={(e) => updateBreak(i, "end", e.target.value)}
                    className="h-8 rounded-sm text-xs font-mono"
                    data-testid={`sched-break-end-${row.role_name}-${i}`}
                  />
                  <Input
                    value={b.label || ""}
                    placeholder="Label"
                    onChange={(e) => updateBreak(i, "label", e.target.value)}
                    className="h-8 rounded-sm text-xs"
                    data-testid={`sched-break-label-${row.role_name}-${i}`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    type="button"
                    onClick={() => removeBreak(i)}
                    className="h-8 w-8 p-0 text-rose-700 hover:bg-rose-50"
                    data-testid={`sched-break-remove-${row.role_name}-${i}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))}
              {(draft.break_windows || []).length === 0 && (
                <div className="text-[11px] text-muted-foreground py-1" data-testid={`sched-no-manual-breaks-${row.role_name}`}>
                  No break windows — agents won&apos;t be frozen for breaks.
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={addBreak}
              className="rounded-sm mt-1 h-7 text-[11px]"
              data-testid={`sched-break-add-${row.role_name}`}
            >
              <Plus size={12} className="mr-1" /> Add break
            </Button>
            {breaksResetByStart && (
              <div className="mt-1 px-2 py-1.5 rounded-sm text-[11px] bg-amber-50 border border-amber-300 text-amber-900" data-testid={`sched-breaks-reset-${row.role_name}`}>
                Start time changed — breaks were reset to the auto rule for the new start. Adjust them below if needed.
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-1">
              Manual breaks are saved exactly as entered. Changing the shift start time re-triggers the auto rule
              (breaks reset), after which you can adjust them again.
            </p>
          </>
        )}
      </div>

      <div className="space-y-2 mb-3 text-xs">
        <label className="flex items-center justify-between cursor-pointer">
          <span>Allow voluntary breaks</span>
          <Switch
            data-testid={`sched-vol-break-${row.role_name}`}
            checked={!!draft.allow_voluntary_break}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, allow_voluntary_break: v }))}
          />
        </label>
        <label className="flex items-center justify-between cursor-pointer">
          <span>Allow access outside shift hours (override)</span>
          <Switch
            data-testid={`sched-bypass-${row.role_name}`}
            checked={!!draft.allow_outside_shift_access}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, allow_outside_shift_access: v }))}
          />
        </label>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-sm"
          data-testid={`sched-save-${row.role_name}`}
        >
          {saving && <Loader2 className="animate-spin mr-1" size={12} />}
          {hasSchedule ? "Save changes" : "Create policy"}
        </Button>
        {hasSchedule && (
          <Button
            size="sm"
            variant="outline"
            onClick={remove}
            disabled={removing}
            className="rounded-sm text-rose-700 hover:bg-rose-50"
            data-testid={`sched-remove-${row.role_name}`}
          >
            <Trash2 size={12} className="mr-1" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function ExtensionRequests({ refreshSignal }) {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [acting, setActing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get("/shift-freeze/extensions/pending"),
        api.get("/shift-freeze/extensions/history?limit=50"),
      ]);
      setPending(p.data.requests || []);
      setHistory(h.data.requests || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshSignal]);

  // Real-time-ish polling — 15s while there are pending requests
  useEffect(() => {
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (rid, action) => {
    setActing((a) => ({ ...a, [rid]: true }));
    try {
      await api.post(`/shift-freeze/extensions/${rid}/${action}`);
      toast.success(action === "approve" ? "Extension approved" : "Extension rejected");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setActing((a) => ({ ...a, [rid]: false }));
    }
  };

  return (
    <div className="border border-border rounded-sm p-4 bg-card mb-6" data-testid="extension-requests-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-base font-semibold">Extension Requests</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
            {pending.length} pending · auto-refreshes every 15s
          </div>
        </div>
        <Button
          size="sm" variant="outline"
          className="rounded-sm"
          onClick={() => setShowHistory((v) => !v)}
          data-testid="ext-history-toggle"
        >
          <History size={13} className="mr-1" /> {showHistory ? "Hide history" : "Show history"}
        </Button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground"><Loader2 className="inline animate-spin mr-1" size={12} /> Loading…</div>
      ) : pending.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground" data-testid="no-pending-extensions">
          No pending extension requests.
        </div>
      ) : (
        <ul className="space-y-2">
          {pending.map((r) => (
            <li key={r.id} className="border border-amber-200 bg-amber-50/50 rounded-sm p-3 flex flex-wrap items-center gap-3" data-testid={`pending-ext-${r.id}`}>
              <Clock size={16} className="text-amber-700" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.user_name} <span className="font-mono text-[10px] text-muted-foreground">· {r.user_email}</span></div>
                <div className="text-[11px] text-muted-foreground">
                  Role: {r.role_label} · Requested {new Date(r.requested_at).toLocaleString()}
                </div>
                {r.reason && <div className="text-[11px] mt-1 italic">&quot;{r.reason}&quot;</div>}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => act(r.id, "approve")}
                  disabled={acting[r.id]}
                  data-testid={`approve-ext-${r.id}`}
                >
                  <CheckCircle2 size={13} className="mr-1" /> Approve
                </Button>
                <Button
                  size="sm" variant="outline" className="rounded-sm text-rose-700 hover:bg-rose-50"
                  onClick={() => act(r.id, "reject")}
                  disabled={acting[r.id]}
                  data-testid={`reject-ext-${r.id}`}
                >
                  <XCircle size={13} className="mr-1" /> Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showHistory && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="ext-history-list">
          <div className="meta-label mb-2">Recent decisions</div>
          {history.length === 0 ? (
            <div className="text-xs text-muted-foreground py-2">No history yet.</div>
          ) : (
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {history.map((r) => (
                <li key={r.id} className="text-[11px] flex items-center gap-2 py-1 border-b border-border last:border-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                    r.status === "approved" ? "bg-emerald-100 text-emerald-800"
                    : r.status === "rejected" ? "bg-rose-100 text-rose-800"
                    : "bg-amber-100 text-amber-800"
                  }`}>{r.status}</span>
                  <span className="font-mono truncate">{r.user_email}</span>
                  <span className="text-muted-foreground ml-auto">{new Date(r.requested_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function AdherenceReport() {
  const today = new Date();
  const isoDay = (d) => d.toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(isoDay(sevenDaysAgo));
  const [to, setTo] = useState(isoDay(today));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/shift-freeze/adherence?date_from=${from}&date_to=${to}`,
      );
      setRows(data.rows || []);
      setMeta({ date_from: data.date_from, date_to: data.date_to });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load adherence");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const fmtMins = (m) => {
    if (m == null) return "—";
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };

  const tone = (pct) => {
    if (pct == null) return "text-muted-foreground";
    if (pct >= 90) return "text-emerald-700 font-medium";
    if (pct >= 70) return "text-amber-700 font-medium";
    return "text-rose-700 font-medium";
  };

  const exportCsv = () => {
    const headers = [
      "User", "Email", "Role", "Scheduled (min)", "Active (min)", "Adherence %",
      "Late-login days", "Shifts", "Leads", "Ext Requested", "Ext Approved", "Ext Rejected",
    ];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        JSON.stringify(r.user_name || ""),
        r.user_email,
        JSON.stringify(r.role_label || ""),
        r.scheduled_minutes,
        r.active_minutes,
        r.adherence_pct == null ? "" : r.adherence_pct,
        r.late_login_days,
        r.shifts,
        r.leads,
        r.ext_requested,
        r.ext_approved,
        r.ext_rejected,
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shift-adherence-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border border-border rounded-sm p-4 bg-card mb-6" data-testid="adherence-report-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display text-base font-semibold flex items-center gap-2">
            <BarChart2 size={16} /> Shift Adherence Report
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
            Active time vs. scheduled · late logins · extension usage
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <div>
            <div className="meta-label">From</div>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-sm w-40 mt-1" data-testid="adherence-date-from" />
          </div>
          <div>
            <div className="meta-label">To</div>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-sm w-40 mt-1" data-testid="adherence-date-to" />
          </div>
          <Button size="sm" onClick={load} className="rounded-sm" data-testid="adherence-refresh">
            {loading ? <Loader2 size={12} className="animate-spin" /> : "Refresh"}
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!rows.length} className="rounded-sm" data-testid="adherence-export">
            Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground"><Loader2 className="inline animate-spin mr-1" size={12} /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">No data for this range.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="adherence-table">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Role</th>
                <th className="text-right p-2">Scheduled</th>
                <th className="text-right p-2">Active</th>
                <th className="text-right p-2">Adherence</th>
                <th className="text-right p-2">Late</th>
                <th className="text-right p-2">Shifts</th>
                <th className="text-right p-2">Leads</th>
                <th className="text-right p-2">Ext (req/ok/rej)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-border hover:bg-secondary/30" data-testid={`adherence-row-${r.user_email}`}>
                  <td className="p-2">
                    <div className="font-medium">{r.user_name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.user_email}</div>
                  </td>
                  <td className="p-2 text-[11px]">{r.role_label}</td>
                  <td className="p-2 text-right">{fmtMins(r.scheduled_minutes)}</td>
                  <td className="p-2 text-right">{fmtMins(r.active_minutes)}</td>
                  <td className={`p-2 text-right ${tone(r.adherence_pct)}`} data-testid={`adherence-pct-${r.user_email}`}>
                    {r.adherence_pct == null ? "—" : `${r.adherence_pct}%`}
                  </td>
                  <td className="p-2 text-right">{r.late_login_days}</td>
                  <td className="p-2 text-right">{r.shifts}</td>
                  <td className="p-2 text-right">{r.leads}</td>
                  <td className="p-2 text-right font-mono text-[11px]">
                    {r.ext_requested}/{r.ext_approved}/{r.ext_rejected}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const ANOMALY_LABELS = {
  no_schedule_for_role_label: "No schedule found for role — user is unlocked",
  schedule_disabled: "Role schedule is disabled — user is unlocked",
  allow_outside_shift_access_on_non_admin: "'Allow access outside shift hours' is ON for a non-admin role",
  per_user_shift_breaks_misaligned: "Per-user shift doesn't overlap role's break windows — auto-healing with dynamic breaks",
  per_user_shift_hours_differ_from_role: "User has a per-user shift override with different hours than the role — freeze may show wrong shift time",
  stale_voluntary_break: "Stale voluntary break (active=true from a past day) — bypassing freeze",
  active_shift_extension: "Approved shift extension is active (informational)",
};

const ANOMALY_TONES = {
  no_schedule_for_role_label: "bg-rose-100 text-rose-800 border-rose-200",
  schedule_disabled: "bg-amber-100 text-amber-800 border-amber-200",
  allow_outside_shift_access_on_non_admin: "bg-amber-100 text-amber-800 border-amber-200",
  per_user_shift_breaks_misaligned: "bg-sky-100 text-sky-800 border-sky-200",
  per_user_shift_hours_differ_from_role: "bg-amber-100 text-amber-800 border-amber-200",
  stale_voluntary_break: "bg-rose-100 text-rose-800 border-rose-200",
  active_shift_extension: "bg-slate-100 text-slate-800 border-slate-200",
};

function FreezeAnomaliesPanel({ refreshSignal }) {
  const [data, setData] = useState({ count: 0, summary: {}, anomalies: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [diagEmail, setDiagEmail] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/shift-freeze/anomalies?limit=200");
      setData(d);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data: d } = await api.post("/shift-freeze/audit");
      toast.success(`Audit complete: ${d.anomalies_found} anomalies found across ${d.scanned} users (${d.auto_healed} will auto-heal).`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Audit failed");
    } finally {
      setRunning(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all anomaly records? Re-runs of the audit will re-detect any that persist.")) return;
    setClearing(true);
    try {
      const { data: d } = await api.post("/shift-freeze/anomalies/clear", {});
      toast.success(`Cleared ${d.deleted} anomaly records.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  const diagnose = async () => {
    if (!diagEmail.trim()) return;
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const { data: d } = await api.get(`/shift-freeze/diagnose?email=${encodeURIComponent(diagEmail.trim())}`);
      setDiagResult(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Diagnose failed");
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div className="border border-border rounded-sm p-4 bg-card mb-6" data-testid="freeze-anomalies-panel">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display text-base font-semibold flex items-center gap-2">
            <ShieldAlert size={16} className="text-rose-600" /> Freeze Anomalies (Self-Healing)
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
            Auto-detects and heals misconfigured freezes so users can&apos;t bypass breaks
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={runAudit} disabled={running} data-testid="freeze-run-audit-btn">
            {running ? <Loader2 size={12} className="animate-spin mr-1" /> : <Wand2 size={12} className="mr-1" />} Run audit now
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm text-rose-700" onClick={clearAll} disabled={clearing || !data.count} data-testid="freeze-clear-anomalies-btn">
            {clearing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Trash2 size={12} className="mr-1" />} Clear all
          </Button>
        </div>
      </div>

      {/* Diagnose specific user */}
      <div className="border border-border rounded-sm p-3 bg-secondary/30 mb-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Diagnose a specific user</div>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            type="email"
            value={diagEmail}
            onChange={(e) => setDiagEmail(e.target.value)}
            placeholder="user@example.com"
            className="h-8 rounded-sm text-xs flex-1 min-w-[200px]"
            data-testid="freeze-diagnose-email-input"
          />
          <Button size="sm" onClick={diagnose} disabled={diagLoading || !diagEmail.trim()} className="rounded-sm" data-testid="freeze-diagnose-btn">
            {diagLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Search size={12} className="mr-1" />} Diagnose
          </Button>
        </div>
        {diagResult && (
          <div className="mt-2 text-[11px]" data-testid="freeze-diagnose-result">
            <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
              <span><strong>Verdict:</strong> <span className={`font-mono px-1.5 py-0.5 rounded-sm ${
                diagResult.verdict?.startsWith("FROZEN") ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
              }`}>{diagResult.verdict}</span></span>
              <span><strong>Role label:</strong> <span className="font-mono">{diagResult.user?.role_label}</span></span>
              <span><strong>TZ:</strong> <span className="font-mono">{diagResult.timezone?.resolved}</span></span>
              <span><strong>Now:</strong> <span className="font-mono">{new Date(diagResult.timezone?.now_local).toLocaleTimeString()}</span></span>
            </div>
            {diagResult.would_bypass_reason && (
              <div className="text-rose-700 mb-1">Bypass reason: <span className="font-mono">{diagResult.would_bypass_reason}</span></div>
            )}
            {(diagResult.user?.per_user_schedule || diagResult.shift_change_override) && (
              <div className="my-2 p-2 border border-amber-200 bg-amber-50 rounded-sm">
                <div className="font-semibold text-amber-800 mb-1">Overrides detected</div>
                {diagResult.user?.per_user_schedule && (
                  <div className="text-[10px] text-amber-800">
                    Per-user override: {" "}
                    <span className="font-mono">
                      {diagResult.user.per_user_schedule.start_time || "?"}–{diagResult.user.per_user_schedule.end_time || "?"}
                      {diagResult.user.per_user_schedule.timezone && ` · TZ ${diagResult.user.per_user_schedule.timezone}`}
                    </span>
                  </div>
                )}
                {diagResult.shift_change_override && (
                  <div className="text-[10px] text-amber-800">
                    Active shift-change → <span className="font-mono">{diagResult.shift_change_override.requested_shift_role}</span> ({diagResult.shift_change_override.effective_date} → {diagResult.shift_change_override.end_date || "open"})
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 h-6 rounded-sm text-[10px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={async () => {
                    if (!window.confirm(
                      `Reset all shift overrides for ${diagResult.user.email}?\n\nThis will clear their per-user shift override AND cancel any pending/approved shift-change requests, so they follow the canonical "${diagResult.user.role_label}" schedule.`
                    )) return;
                    try {
                      const { data: res } = await api.post("/shift-freeze/reset-user-overrides", { user_id: diagResult.user.id });
                      toast.success(
                        `Reset ${diagResult.user.email}: `
                        + `${res.cleared_per_user_schedule ? "override cleared, " : ""}`
                        + `${res.cancelled_shift_changes} shift-change${res.cancelled_shift_changes === 1 ? "" : "s"} cancelled.`,
                        { duration: 8000 },
                      );
                      setDiagResult(null);
                      setDiagEmail("");
                      load();
                    } catch (e) {
                      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
                    }
                  }}
                  data-testid="freeze-diagnose-reset-btn"
                >
                  Reset all overrides for this user
                </Button>
              </div>
            )}
            <details className="mt-1">
              <summary className="cursor-pointer text-muted-foreground text-[10px]">Full payload</summary>
              <pre className="text-[10px] bg-slate-50 p-2 rounded-sm overflow-x-auto mt-1 max-h-80">{JSON.stringify(diagResult, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground"><Loader2 className="inline animate-spin mr-1" size={12} /> Loading anomalies…</div>
      ) : data.count === 0 ? (
        <div className="py-6 text-center text-xs text-emerald-700 font-medium" data-testid="freeze-no-anomalies">
          <CheckCircle2 className="inline mr-1" size={14} /> No anomalies detected. All non-admin users are being evaluated correctly.
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(data.summary).map(([k, v]) => (
              <span key={k} className={`text-[10px] px-2 py-1 rounded-sm border ${ANOMALY_TONES[k] || "bg-slate-100"}`}>
                {ANOMALY_LABELS[k] || k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-xs" data-testid="freeze-anomalies-table">
              <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-muted-foreground sticky top-0">
                <tr>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Role Label</th>
                  <th className="text-left p-2">Anomaly</th>
                  <th className="text-left p-2">Auto-healed</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-right p-2">Detected</th>
                </tr>
              </thead>
              <tbody>
                {data.anomalies.map((a) => (
                  <tr key={a.id} className="border-b border-border">
                    <td className="p-2">
                      <div className="font-medium">{a.user_email || "—"}</div>
                    </td>
                    <td className="p-2 font-mono text-[10px]">{a.role_label || "—"}</td>
                    <td className="p-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${ANOMALY_TONES[a.kind] || "bg-slate-100"}`}>{a.kind}</span>
                    </td>
                    <td className="p-2">{a.auto_healed ? <span className="text-emerald-700 font-medium">Yes</span> : <span className="text-rose-700">No — needs admin fix</span>}</td>
                    <td className="p-2">
                      {a.kind === "stale_voluntary_break" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 rounded-sm text-[10px] px-2"
                          onClick={async () => {
                            try {
                              const { data: res } = await api.post("/shift-freeze/voluntary-break/force-end", { user_id: a.user_id });
                              toast.success(`Ended ${res.ended} stale break for ${a.user_email}`);
                              load();
                            } catch (e) {
                              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Force-end failed");
                            }
                          }}
                          data-testid={`freeze-force-end-vb-${a.user_id}`}
                        >
                          Force-end break
                        </Button>
                      )}
                      {(a.kind === "per_user_shift_hours_differ_from_role"
                        || a.kind === "per_user_shift_breaks_misaligned") && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 rounded-sm text-[10px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={async () => {
                            if (!window.confirm(
                              `Reset all shift overrides for ${a.user_email}?\n\nThis will:\n\n• Clear their per-user shift override (start/end/days/timezone)\n• Cancel any pending or approved shift-change requests still active today\n• Purge related anomalies from this panel\n\nAfter this the user follows the CANONICAL "${a.role_label}" schedule.`
                            )) return;
                            try {
                              const { data: res } = await api.post("/shift-freeze/reset-user-overrides", { user_id: a.user_id, user_email: a.user_email });
                              toast.success(
                                `Reset ${a.user_email}: `
                                + `${res.cleared_per_user_schedule ? "schedule cleared, " : ""}`
                                + `${res.cancelled_shift_changes} shift-change${res.cancelled_shift_changes === 1 ? "" : "s"} cancelled, `
                                + `${res.anomalies_purged} anomal${res.anomalies_purged === 1 ? "y" : "ies"} purged.`,
                                { duration: 8000 },
                              );
                              load();
                            } catch (e) {
                              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
                            }
                          }}
                          data-testid={`freeze-reset-overrides-${a.user_id}`}
                        >
                          Reset shift overrides
                        </Button>
                      )}
                      {(a.kind === "no_schedule_for_role_label" || a.kind === "allow_outside_shift_access_on_non_admin" || a.kind === "schedule_disabled") && (
                        <span className="text-[10px] text-muted-foreground italic">Fix schedule above</span>
                      )}
                    </td>
                    <td className="p-2 text-right text-[10px] text-muted-foreground">{new Date(a.detected_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminShiftSchedules() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shift-freeze/schedules");
      setRows(data.roles || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = () => {
    load();
    setRefreshTick((t) => t + 1);
  };

  const sortedRows = useMemo(() => {
    const withSched = rows.filter(r => r.schedule);
    const withoutSched = rows.filter(r => !r.schedule);
    return [...withSched, ...withoutSched];
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="admin-shift-schedules">
      <div>
        <h3 className="font-display text-lg font-semibold">Shift Schedules & App Freeze</h3>
        <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
          Lock non-admin users out of the app outside their shift hours, on non-working days,
          and during break windows. Built-in admins always retain access. Users may request a
          one-time 20-minute extension per shift; admins approve inline below.
        </p>
        <div className="text-xs mt-2 p-2 border border-sky-200 bg-sky-50 text-sky-900 rounded-sm max-w-3xl" data-testid="shift-schedules-info">
          <strong>How this connects to the Shifts tab:</strong> these per-role schedules also drive the
          Shifts tab counters (scheduled start/end, late-login flag, break-overrun checks) for every
          user whose role matches — no need to re-enter the schedule per user. The Shifts tab&apos;s
          per-user schedule override is still available and takes precedence when set.
          Overnight shifts (e.g. <span className="font-mono">17:50 → 03:00</span>) are supported —
          the end time wrapping past midnight is interpreted as next-day.
        </div>
      </div>

      <FreezeAnomaliesPanel refreshSignal={refreshTick} />

      <ExtensionRequests refreshSignal={refreshTick} />

      <AdherenceReport />

      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Loading schedules…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sortedRows.map((row) => (
            <RoleCard key={row.role_name} row={row} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}
